import React, { useEffect, Fragment } from 'react';
import { Grid, Icon, Typography } from '@material-ui/core';
import classnames from 'classnames';

import { FileDownload } from '@klp/filedownload';

import PDFIcon from '../assets/Adobe_PDF.svg';
import { MimeTypes } from '../classes';
import type { FileObject } from '../classes';
import { useFileContext } from '../context/FileContext';
import { useLazyGetFileByPathQuery } from '../rtkApi';

import './FilePreview.scss';

const icons = {
    'application/pdf': PDFIcon,
};

export interface IOwnProps {
    fileObject: FileObject;
    edit?: boolean;
    onRemove?: (fileObject: FileObject) => void;
    onClick?: (fileObject: FileObject) => void;
    fileContentField?: string;
}

export const FilePreview: React.FC<IOwnProps> = ({
    fileObject,
    edit,
    onRemove,
    onClick,
    fileContentField,
}) => {
    const { downloadQueue, addToDownloadQueue } = useFileContext();
    const [getFile] = useLazyGetFileByPathQuery();

    useEffect(() => {
        const loadFile = async () => {
            if (
                !fileObject.fileContent &&
                fileObject.type &&
                MimeTypes.image.includes(fileObject.type)
            ) {
                try {
                    // Load full file content for images using RTK Query
                    const result = await getFile(fileObject).unwrap();
                    // Update the context with the loaded file
                    addToDownloadQueue(result);
                } catch (error) {
                    // Error silently - file preview will show placeholder
                }
            } else if (fileObject.type && !fileObject.local) {
                // Add documents to download queue even without loading content
                // This is needed for delete functionality to work
                addToDownloadQueue(fileObject);
            }
        };

        loadFile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileObject.fileContent, fileObject.type, fileObject.pathIndex]);

    const handleRemove = () => {
        if (onRemove) {
            onRemove(fileObject);
        }
    };

    const handleClick = () => {
        if (onClick) {
            onClick(fileObject);
        }
    };

    const renderPreviewImage = (file: FileObject, backgroundImage: string) => {
        const classNames = classnames('Gallery__ItemPicture', {
            'Gallery__ItemPicture--toDelete': edit && file.toDelete,
            'Gallery__ItemPicture--toBeUploaded': edit && file.local,
            'Gallery__ItemPicture--Preview': !edit,
        });

        return (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
                {edit ? (
                    <div
                        className={classNames}
                        style={{ backgroundImage: backgroundImage }}
                        onClick={!edit ? handleClick : undefined}
                    >
                        <Icon
                            className="Gallery__ItemPicture--Remove"
                            onClick={handleRemove}
                        >
                            remove_circle
                        </Icon>

                        {file.toDelete && (
                            <div className="pictureMarker">
                                <Icon className="pictureMarker__Icon pictureMarker__Icon--delete">
                                    delete
                                </Icon>
                            </div>
                        )}
                        {file.local && (
                            <div className="pictureMarker">
                                <Icon className="pictureMarker__Icon pictureMarker__Icon--upload">
                                    cloud_upload
                                </Icon>
                            </div>
                        )}
                    </div>
                ) : (
                    <FileDownload
                        path={file.filePath}
                        name={file.fileName}
                        fileContentField={fileContentField}
                        preview={true}
                    >
                        <div
                            className={classNames}
                            style={{ backgroundImage: backgroundImage }}
                        />
                    </FileDownload>
                )}
            </>
        );
    };

    const renderPreviewDocument = (file: FileObject, iconSrc: string) => {
        const classNames = classnames(
            'Gallery__ItemPicture',
            'Gallery__ItemPicture--Icon',
            'Gallery__ItemPicture--Preview',
            {
                'Gallery__ItemPicture--toDelete': edit && file.toDelete,
            },
        );

        return (
            <>
                {edit && (
                    <div>
                        <div className={classNames}>
                            <img src={iconSrc} alt={file.fileName} />
                            <Icon
                                className="Gallery__ItemPicture--Remove"
                                onClick={handleRemove}
                            >
                                remove_circle
                            </Icon>
                            {file.toDelete && (
                                <div className="pictureMarker">
                                    <Icon className="pictureMarker__Icon pictureMarker__Icon--delete">
                                        delete
                                    </Icon>
                                </div>
                            )}
                            {file.local && (
                                <div className="pictureMarker">
                                    <Icon className="pictureMarker__Icon pictureMarker__Icon--upload">
                                        cloud_upload
                                    </Icon>
                                </div>
                            )}
                        </div>
                        <Typography
                            className="Gallery__DocumentCaption"
                            variant="caption"
                        >
                            {file.fileName}
                        </Typography>
                    </div>
                )}
                {!edit && (
                    <FileDownload
                        path={file.filePath}
                        name={file.fileName}
                        fileContentField={fileContentField}
                        preview={true}
                    >
                        <>
                            <div className={classNames}>
                                <img src={iconSrc} alt={file.fileName} />
                            </div>
                            <Typography
                                className="Gallery__DocumentCaption"
                                variant="caption"
                            >
                                {file.fileName}
                            </Typography>
                        </>
                    </FileDownload>
                )}
            </>
        );
    };

    const renderPreview = (file: FileObject) => {
        let backgroundImage;
        if (file.type) {
            if (MimeTypes.image.includes(file.type)) {
                if (file.local) {
                    backgroundImage = `url(${file.fileContent})`;
                } else {
                    backgroundImage = `url(data:${file.type};base64,${file.fileContent})`;
                }
                return renderPreviewImage(file, backgroundImage);
            }
            if (MimeTypes.document.includes(file.type)) {
                const icon = icons[file.type] || icons['application/pdf'];
                return renderPreviewDocument(file, icon);
            }
        }
        return null;
    };

    const renderPlaceholder = () => {
        const classNames = classnames(
            'Gallery__ItemPicture',
            'Gallery__ItemPicture--Placeholder',
            'Gallery__ItemPicture--Loading',
        );
        return (
            <div className={classNames} onClick={handleClick}>
                <Icon className="Gallery__ItemIcon">photo</Icon>
            </div>
        );
    };

    const { pathIndex, queueName } = fileObject;
    let preview: JSX.Element | null = null;

    if (fileObject?.fileContent) {
        preview = renderPreview(fileObject);
    } else if (
        downloadQueue &&
        queueName &&
        pathIndex &&
        downloadQueue[queueName]?.[pathIndex] !== undefined
    ) {
        preview = renderPreview(downloadQueue[queueName][pathIndex]);
    } else if (
        fileObject.type &&
        MimeTypes.document.includes(fileObject.type)
    ) {
        preview = renderPreview(fileObject);
    } else {
        preview = renderPlaceholder();
    }

    return (
        <Grid item={true} xs={6} sm={4} md={3} className="Gallery__Item">
            {preview}
        </Grid>
    );
};
